import { Close, MedicalInformation } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { HPOSchema } from '@/zod_schemas';

interface AssociatedGenesDialogProps {
  viewDetails: HPOSchema | undefined;
  handleClose: () => void;
}

const AssociatedGenesDialog = ({
  handleClose,
  viewDetails,
}: AssociatedGenesDialogProps) => (
  <Dialog
    open={Boolean(viewDetails)}
    onClose={handleClose}
    fullWidth
    maxWidth="sm"
  >
    <DialogTitle
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 'bold',
        textAlign: 'center',
        fontSize: '1.2rem', // Reduces title font size
        py: 1.5, // Adjusts vertical padding (less height)
        position: 'relative',
      }}
    >
      <MedicalInformation sx={{ verticalAlign: 'middle', mr: 1 }} />
      <strong>Term Identifiers:</strong>
      {viewDetails ? viewDetails.hpo_id : 'HPO Details'}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'primary.contrastText',
        }}
      >
        <Close />
      </IconButton>
    </DialogTitle>

    <DialogContent
      sx={{
        bgcolor: 'background.default',
        p: 3,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {viewDetails && (
        <Paper
          elevation={3}
          sx={{
            marginTop: 3,
            p: 3,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            width: '100%',
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h6"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            {viewDetails.name}
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Stack spacing={2} alignItems="flex-start">
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                Associated Genes
              </Typography>

              {viewDetails.genes?.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {viewDetails.genes.map((gene) => (
                    <Chip
                      key={gene.name}
                      label={gene.name}
                      size="small"
                      color="primary"
                      variant="filled"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No genes associated with this variant
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Paper>
      )}
    </DialogContent>
  </Dialog>
);

export default AssociatedGenesDialog;
